import { Injectable } from '@angular/core';
import { RemittanceFileRecord } from '../../../models/remittances/remittance-file-record';
import { parse } from 'csv-parse';
import { StringExtensions } from '../../../utils/string.extensions';
import { MembersFileRecord } from '../../shared/components/batch-add-members-file-upload/interfaces/members-file-record';

@Injectable()
export class FileToJsonService {
  public convertFileToJson(file: File): Promise<RemittanceFileRecord[]> {
    const isXLSX = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const isCSV = file.type === 'text/csv' || file.name.endsWith('.dat');
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async e => {
        const content = e.target?.result as ArrayBuffer;
        if (isCSV) {
          resolve(this.convertCsvToJson(this.decodeCsv(content)));
        } else if (isXLSX) {
          resolve(await this.convertXlsxToJson(content));
        }
      };
      reader.onerror = () => {
        reject($localize`Error reading file.`);
      };

      if (isCSV || isXLSX) {
        reader.readAsArrayBuffer(file);
      }
    });
  }

  private decodeCsv(content: ArrayBuffer): string {
    try {
      return new TextDecoder('utf-8', { fatal: true }).decode(content);
    } catch {
      return new TextDecoder('windows-1252').decode(content);
    }
  }

  private convertCsvToJson(csvData: string): Promise<RemittanceFileRecord[]> {
    return new Promise((resolve, reject) => {
      parse(
        csvData,
        {
          columns: true,
          fromLine: 1,
          trim: true,
          cast: (value, context) => {
            switch (context.column) {
              case 'AMOUNT':
              case 'SALARY':
                return this.parseCurrencyString(value);
              case 'ENTRY CODE':
                return parseInt(value, 10);
              case 'DESIGNATED UNIT ID':
                return this.parseDesignatedUnitId(value);
              case 'FIRST NAME':
              case 'LAST NAME':
                return this.parseName(value);
              default:
                return value;
            }
          }
        },
        (error, records) => {
          if (error) {
            reject($localize`Error reading file.`);
          } else {
            resolve(records);
          }
        }
      );
    });
  }

  private parseName(value: string): string | null {
    const trimmedValue = value.trim();
    return trimmedValue.includes('\uFFFD') ? null : trimmedValue;
  }

  private parseCurrencyString(value: string): number | null | string {
    if (value.trim() === '') return value;
    const formattedValue = StringExtensions.removeNonNumeric(value);
    return isNaN(parseFloat(formattedValue)) ? null : parseFloat(formattedValue);
  }

  private parseDesignatedUnitId(value: string): string | null {
    const trimmedValue = value.trim();
    if (trimmedValue === '') return trimmedValue;
    return /^[0-9]{1,8}$/.test(trimmedValue) ? trimmedValue : null;
  }

  private async convertXlsxToJson(content: ArrayBuffer): Promise<RemittanceFileRecord[]> {
    const { read, utils } = await import('xlsx');
    const workbook = read(content, { type: 'buffer' });
    const sheetName = workbook.SheetNames.last();
    const sheet = workbook.Sheets[sheetName];
    const rows: unknown[][] = utils.sheet_to_json(sheet, { header: 1, raw: true });
    const headerRowIndex = Math.max(
      0,
      rows.findIndex(row => this.isRemittanceHeaderRow(row))
    );
    // A unit ID cell can hold the number 20 formatted to display as 0020, so read the displayed text to keep leading zeros
    const formattedRecords = utils.sheet_to_json<Record<string, unknown>>(sheet, { range: headerRowIndex, raw: false });
    const jsonData = utils.sheet_to_json(sheet, { range: headerRowIndex, raw: true }).map((record: any, index) => {
      Object.keys(record).forEach((key: any) => {
        if (key === 'SALARY') {
          record[key] = this.parseCurrencyString(record[key].toString());
        } else if (key === 'DESIGNATED UNIT ID') {
          record[key] = this.parseDesignatedUnitId(String(formattedRecords[index]?.[key] ?? record[key]));
        } else if (typeof record[key] === 'string') {
          record[key] = record[key].trim();
        }
      });
      return record;
    });
    return Promise.resolve(jsonData);
  }

  private isRemittanceHeaderRow(row: unknown[]): boolean {
    return row.some(cell => String(cell).trim().toUpperCase() === 'SIN');
  }

  // Member records
  private convertMembersCsvToJson(csvData: string): Promise<MembersFileRecord[]> {
    return new Promise((resolve, reject) => {
      parse(
        csvData,
        {
          columns: true,
          fromLine: 1,
          trim: true
        },
        (error, records) => {
          if (error) {
            reject($localize`Error reading file.`);
          } else {
            resolve(records);
          }
        }
      );
    });
  }

  private async convertMembersXlsxToJson(content: ArrayBuffer): Promise<MembersFileRecord[]> {
    const { read, utils } = await import('xlsx');
    const workbook = read(content, { type: 'buffer' });
    const sheetName = workbook.SheetNames.last();
    const sheet = workbook.Sheets[sheetName];
    const jsonData = utils.sheet_to_json(sheet, { range: 1, raw: true }).map((record: any) => {
      Object.keys(record).forEach((key: any) => {
        if (typeof record[key] === 'string') {
          record[key] = record[key].trim();
        }
      });
      return record;
    });
    return Promise.resolve(jsonData);
  }

  public convertMemberFileToJson(file: File): Promise<MembersFileRecord[]> {
    const isXLSX = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const isCSV = file.type === 'text/csv' || file.name.endsWith('.dat');
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async e => {
        const content = e.target?.result;
        if (isCSV) {
          resolve(this.convertMembersCsvToJson(content as string));
        } else if (isXLSX) {
          resolve(await this.convertMembersXlsxToJson(content as ArrayBuffer));
        }
      };
      reader.onerror = () => {
        reject($localize`Error reading file.`);
      };

      if (isCSV) {
        reader.readAsText(file);
      } else if (isXLSX) {
        reader.readAsArrayBuffer(file);
      }
    });
  }
}
